import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import apiService from '../services/api';
export const useJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    total_jobs: 0,
    status_counts: {0: 0, 1: 0, 2: 0, 3: 0},
    scraper_counts: {},
    recent_activity: []
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    limit: 10,
    has_next: false,
    has_prev: false
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    jobId: null,
    title: '',
    description: ''
  });
  
  const fetchJobs = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const data = await apiService.getJobs(page, 10, search);
      setJobs(data.jobs || []);
      setPagination(data.pagination || pagination);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const data = await apiService.getJobsStats();
      setDashboardStats(data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };
  const showDeleteConfirmation = (jobId, jobName, scraperType) => {
    setConfirmDialog({
      open: true,
      jobId,
      title: 'Delete Scraping Job',
      description: `Are you sure you want to delete this ${scraperType} job "${jobName}"? This action cannot be undone.`
    });
  };
  const deleteJob = async (jobId) => {
    try {
      await apiService.deleteJob(jobId);
      fetchJobs();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete job' };
    }
  };
  const handleConfirmDelete = async () => {
    if (confirmDialog.jobId) {
      const result = await deleteJob(confirmDialog.jobId);
      if (result.success) {
        // Job deleted successfully
      } else {
        // Failed to delete job
      }
    }
  };
  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      jobId: null,
      title: '',
      description: ''
    });
  };
  const getJobResults = async (jobId, opts = {}) => {
    try {
      const hasPaging = opts && (opts.page != null || opts.limit != null);
      if (hasPaging) {
        const page = opts.page ?? 1;
        const limit = opts.limit ?? 20;
        const data = await apiService.getJobResults(jobId, page, limit);
        return {
          success: true,
          results: data.results || [],
          pagination: data.pagination,
        };
      }
      const all = [];
      let p = 1;
      const per = 500;
      while (true) {
        const data = await apiService.getJobResults(jobId, p, per);
        const chunk = data.results || [];
        all.push(...chunk);
        if (!data.pagination?.has_next || chunk.length === 0) break;
        p += 1;
        if (p > 500) break;
      }
      return { success: true, results: all };
    } catch (error) {
      return { success: false, error: 'Failed to fetch results' };
    }
  };
  const downloadResults = (results, jobType) => {
    if (!results || results.length === 0) {
      return;
    }
    
    // Helper function to detect if a value is a URL
    const isURL = (value) => {
      if (!value || typeof value !== 'string') return false;
      try {
        const urlPattern = /^(https?:\/\/|www\.)[^\s]+$/i;
        return urlPattern.test(value.trim());
      } catch {
        return false;
      }
    };
    
    try {
      // Get headers from the first result
      const headers = Object.keys(results[0] || {});
      
      // Prepare data for Excel with proper structure
      const worksheetData = [];
      
      // Add header row
      worksheetData.push(headers);
      
      // Add data rows
      results.forEach(row => {
        const rowData = headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          return String(value);
        });
        worksheetData.push(rowData);
      });
      
      // Create a new workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Identify URL columns and add hyperlinks
      const urlColumns = ['url', 'link', 'website', 'property url', 'property_url', 'listing_url'];
      
      headers.forEach((header, colIndex) => {
        const isUrlColumn = urlColumns.some(col => header.toLowerCase().includes(col));
        
        if (isUrlColumn) {
          // Process each cell in this column (starting from row 1, skipping header)
          results.forEach((row, rowIndex) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
            const value = row[header];
            
            if (value && typeof value === 'string' && (isURL(value) || isUrlColumn)) {
              const href = value.startsWith('http') ? value : `https://${value}`;
              
              // Set cell value and hyperlink
              if (!worksheet[cellRef]) {
                worksheet[cellRef] = {};
              }
              worksheet[cellRef].v = value;
              worksheet[cellRef].l = { Target: href, Tooltip: value };
              worksheet[cellRef].s = {
                font: { color: { rgb: "0563C1" }, underline: true }
              };
            }
          });
        }
      });
      
      // Set column widths for better readability
      const columnWidths = headers.map(header => {
        const maxLength = Math.max(
          header.length,
          ...results.slice(0, 100).map(row => {
            const value = row[header];
            return value ? String(value).length : 0;
          })
        );
        return { wch: Math.min(maxLength + 2, 50) }; // Max width of 50 characters
      });
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Results');
      
      // Generate Excel file and trigger download
      XLSX.writeFile(workbook, `${jobType}_results.xlsx`);
      
    } catch (error) {
      console.error('Error generating Excel file:', error);
      alert('Error generating Excel file. Please try again.');
    }
  };
  return {
    jobs,
    setJobs,
    fetchJobs,
    dashboardStats,
    fetchDashboardStats,
    pagination,
    loading,
    searchTerm,
    setSearchTerm,
    showDeleteConfirmation,
    deleteJob,
    getJobResults,
    downloadResults,
    confirmDialog,
    handleConfirmDelete,
    closeConfirmDialog
  };
}; 